import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {ButtonBar, SingleSelectField, useSnackbar} from "@dain/mui-components";
import {useNavigate, useParams} from "react-router-dom";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {SUBMISSION_AUTHORIZATION_COMPLETION, SUBMISSIONS} from "../../../AppRoutes";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {SubmissionDto} from "../../../client/submission/SubmissionApiClient";
import {DetailsTable} from "../../../common/details/DetailsTable";
import {DetailsTextRow} from "../../../common/details/DetailsTextRow";
import {DateTimeFormatter} from "../../../common/DateTimeFormatter";
import {Grid, Stack, Typography} from "@mui/material";
import {DigitalActionAuthMockResult} from "./DigitalActionAuthMockResult";
import {DigitalActionAuthMockResultFormatter} from "./DigitalActionAuthMockResultFormatter";
import {
    DigitalActionAuthorizationMockedResultDto,
    DigitalActionAuthorizationResult
} from "../../../client/submission/SubmissionAuthorizationApiClient";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import Page from "../../../common/page/Page";
import {SubmissionAuthorizationCompletionPageParam} from "./SubmissionAuthorizationCompletionPage";
import Button from "../../../common/button/Button";
import {CatalogType} from "../../../client/catalog/CatalogType";

enum PageState {
    IDLE,
    SAVE_IN_PROGRESS,
}

export function DigitalActionAuthorizationMockPage() {
    const {t} = useTranslation();
    const snackbar = useSnackbar();
    const {submissionClient, submissionAuthorizationClient} = useClientLocator();
    const navigate = useNavigate();

    const {submissionId, digitalActionId} = useParams();

    const initialized = useRef(false);
    const [submission, setSubmission] = useState<SubmissionDto>();

    const [mockResult, setMockResult] = useState<DigitalActionAuthMockResult | null>(null);
    const [pageState, setPageState] = useState(PageState.IDLE);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            if (submissionId && !isNaN(+submissionId)) {
                submissionClient.findById(+submissionId)
                    .then((submission) => {
                        if (submission && submission.digitalActionAuthorization?.digitalActionId === digitalActionId) {
                            setSubmission(submission);
                        } else {
                            navigate(SUBMISSIONS);
                        }
                    })
                    .catch((err) => {
                        snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err);
                    });
            } else {
                navigate(SUBMISSIONS);
            }
        }
    }, [submissionId, submissionClient, digitalActionId, navigate, t, snackbar]);

    function submitResult() {
        if (submission) {
            setPageState(PageState.SAVE_IN_PROGRESS);
            var submissionId = submission.id.toString();
            var digitalActionId = submission.digitalActionAuthorization?.digitalActionId!;

            var errorCode: string | undefined;
            switch (mockResult) {
                case DigitalActionAuthMockResult.UNSPECIFIED_ERROR:
                    errorCode = "0";
                    break;
                case DigitalActionAuthMockResult.IDENTITY_MISMATCH:
                    errorCode = "2";
                    break;
                case DigitalActionAuthMockResult.LOGIN_ERROR:
                    errorCode = "4";
                    break;
                default:
                    errorCode = undefined;
            }

            const simulateTemporaryError = mockResult === DigitalActionAuthMockResult.TEMPORARY_FAILURE;

            var mockedResult: DigitalActionAuthorizationMockedResultDto = {
                digitalActionId: digitalActionId,
                result: simulateTemporaryError
                    ? undefined
                    : (mockResult === DigitalActionAuthMockResult.AUTHORIZED ? DigitalActionAuthorizationResult.SUCCESS : DigitalActionAuthorizationResult.ERROR),
                errorCode: errorCode,
                simulateTemporaryError: simulateTemporaryError
            };

            // The completion page navigation always needs a concrete Result value; when a temporary
            // outage is simulated, the BE returns 503 for this digitalActionId regardless of it.
            const completionResult = mockedResult.result ?? DigitalActionAuthorizationResult.SUCCESS;

            submissionAuthorizationClient.setDigitalActionAuthorizationMockedResult(submissionId, mockedResult)
                .then(() => navigateToAuthorizationCompletion(digitalActionId, completionResult, errorCode))
                .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
        }
    }

    function navigateToAuthorizationCompletion(digitalActionId: string, result: DigitalActionAuthorizationResult, errorCode: string | undefined) {
        const completionPath = SUBMISSION_AUTHORIZATION_COMPLETION;
        const searchParams = new URLSearchParams();
        searchParams.append(SubmissionAuthorizationCompletionPageParam.DIGITAL_ACTION_ID_PARAM, digitalActionId);
        searchParams.append(SubmissionAuthorizationCompletionPageParam.RESULT_PARAM, result);
        if (errorCode) {
            searchParams.append(SubmissionAuthorizationCompletionPageParam.ERROR_CODE_PARAM, errorCode);
        }
        navigate({pathname: completionPath, search: searchParams.toString()});
    }

    const mockResultOptions = Object.values(DigitalActionAuthMockResult) as DigitalActionAuthMockResult[];

    function getMockResultOptionLabel(result: DigitalActionAuthMockResult) : string {
        return DigitalActionAuthMockResultFormatter.formatResult(result, t);
    }

    if (!submission) {
        return null;
    }

    return (
        <Page title={t(SubmissionTranslKey.DIGITAL_ACTION_AUTHORIZATION_MOCK)}>
            <Stack spacing={2} padding={1}>

                <DetailsTable header={t(SubmissionTranslKey.SUBMISSION_DETAILS)}>
                    <DetailsTextRow label={t(SubmissionTranslKey.DOCUMENT_TYPE)}
                                    value={t(submission.documentMetadata.type?.code ?? '', {ns: CatalogType.DOCUMENT_TYPE})}/>
                    <DetailsTextRow label={t(SubmissionTranslKey.SUBMITTED_AT)} value={DateTimeFormatter.formatDateTime(submission.submittedAt)}/>
                    <DetailsTextRow label={t(SubmissionTranslKey.DIGITAL_ACTION_ID)} value={submission.digitalActionAuthorization?.digitalActionId}/>
                </DetailsTable>

                <Typography variant={"h5"}>{t(SubmissionTranslKey.AUTHORIZATION_RESULT)}</Typography>
                <Grid container>
                    <Grid item xs={8}>
                        <SingleSelectField label={t(SubmissionTranslKey.AUTHORIZATION_RESULT)}
                                     options={mockResultOptions}
                                     getOptionLabel={getMockResultOptionLabel}
                                     value={mockResult}
                                     onChange={(value) => setMockResult(value as DigitalActionAuthMockResult)}/>
                    </Grid>
                </Grid>

                <ButtonBar>
                    <Button variant={"primary"}
                            inProgress={pageState === PageState.SAVE_IN_PROGRESS}
                            disabled={mockResult === null}
                            onClick={() => submitResult()}>
                        {t(SubmissionTranslKey.SUBMIT_AUTHORIZATION_RESULT)}
                    </Button>
                </ButtonBar>
            </Stack>
        </Page>
    );
}
